import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { conversationsAPI} from "api/Interactions/ConversationsAPI";
import { Conversation } from "api/Interactions/InteractionsInterfaces";
import ConversationEmptyPage from "./ConversationsEmptyPage";
import GenericDetailPage from "components/Interactions/GenericDetailPage";
import { baseURLFrontend } from "api/APIClient";

interface Person {
  id: number;
  first_name: string;
  last_name: string;
  phone?: string;
  conversation?: string;
}

interface ConversationDetailProps {
  conversationId: number;
  onClose?: () => void;
}

const ConversationDetail: React.FC<ConversationDetailProps> = ({ conversationId, onClose }) => {
  const theme = useTheme();
  const [record, setRecord] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const numberOfConversations = record?.people?.length || 0;
  const costPerConversation = 0.05;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await conversationsAPI.getById(conversationId);
        setRecord(data as Conversation);
      } catch (err) {
        console.error("Error loading conversation:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [conversationId]);

  const handleUpdate = async (id: number, field: keyof Conversation, value: any) => {
    if (typeof id !== "number") return;
    try {
      setSaving(true);
      const updated = await conversationsAPI.updateField(id, field, value);
      setRecord(updated as Conversation);
    } catch (err) {
      console.error(`Error updating field "${String(field)}":`, err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFulfilled = async () => {
    if (!record) return;
    await handleUpdate(record.id, "pathways_fulfilled", !record.pathways_fulfilled);
  };

  if (loading) return null;
  if (!record) return <ConversationEmptyPage />;
  
  return (
    <GenericDetailPage
      record={record}
      loading={loading}
      saving={saving}
      onClose={onClose}
      onUpdate={handleUpdate}
      onToggleFulfilled={handleToggleFulfilled}
      fulfilled={!!record.pathways_fulfilled}
      fulfillmentDetailsLabel="Conversations"
      numberOfItems={numberOfConversations}
      costPerItem={costPerConversation}
      showSaveButton
      saveButtonColor={theme.custom.modules.interactions.primary.dark}
      fulfilledByPathwaysAvailable={false}
      registration={false}
      registrationLink={baseURLFrontend + "/conversations/register/"+record.public_token+"/"}
      logging={true}
      loggingLink={baseURLFrontend + "/conversations/"+ record.public_token+"/"}
      loggingLabel="Notes"
      contentDetailFields={[
        { label: "Date of Conversation", value: record.date_created, field: "date", type: "datetime" },
        { label: "Location", value: record.location, field: "location" },
        { label: "Allow team members to add people from your database", value: record.external_access_to_people_database, field: "external_access_to_people_database", type: "boolean" },
        { label: "Notes for Leaders", value: record.notes, field: "notes", multiline: true, rows: 4  },

      ]}
    />
  );
};

export default ConversationDetail;
